import { Link } from 'react-router-dom';
import { date as fmtDate, hours as fmtHours, num, relativeDays } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { StatusChip } from '@/components/ui/StatusChip';
import type { DashboardSummary } from '@/types/models';

interface Item {
  key: string;
  tone: 'red' | 'amber';
  what: string;
  detail: string;
  to: string;
}

/**
 * The spec's test of this system is that the manager opens it in the morning
 * and knows what to do. Everything below this panel is a number; this panel
 * is the decision.
 *
 * It only orders and phrases what dashboard_summary() already returned — no
 * threshold is invented here, because a threshold in the client is a second
 * definition of the truth.
 */
function decisions(d: DashboardSummary): Item[] {
  const out: Item[] = [];
  const { today, blast, maintenance, parts } = d;

  for (const m of d.machines_down_detail) {
    out.push({
      key: `down-${m.fleet_no}`,
      tone: 'red',
      what: `${m.fleet_no} is down`,
      detail: [
        m.fault ?? 'fault not recorded',
        m.hours_down != null ? `${fmtHours(m.hours_down)} h so far` : null,
        m.blocking_parts
          ? `waiting on ${m.blocking_parts}${m.eta ? ` · ETA ${relativeDays(m.eta)}` : ' · not yet ordered'}`
          : null,
      ].filter(Boolean).join(' · '),
      to: '/breakdowns',
    });
  }

  if (blast.status_colour === 'red' || blast.status_colour === 'amber') {
    out.push({
      key: 'blast',
      tone: blast.status_colour,
      what: blast.status_colour === 'red' ? 'Blast now' : 'Plan the next blast',
      detail: [
        blast.remaining_tonnes != null ? `${num(blast.remaining_tonnes)} t on the ground` : null,
        blast.production_days_remaining != null
          ? `${num(blast.production_days_remaining, 1)} production days` : null,
        blast.expected_depletion_date ? `runs out ${fmtDate(blast.expected_depletion_date)}` : null,
      ].filter(Boolean).join(' · '),
      to: '/blasts',
    });
  }

  if (parts.parts_delaying_repairs > 0) {
    out.push({
      key: 'parts-blocking',
      tone: 'red',
      what: `${parts.parts_delaying_repairs} part${parts.parts_delaying_repairs === 1 ? '' : 's'} delaying a repair`,
      detail: 'A machine is down and the part it needs is not on the shelf',
      to: '/parts',
    });
  }

  if (maintenance.services_overdue > 0) {
    out.push({
      key: 'svc-overdue',
      tone: 'red',
      what: `${maintenance.services_overdue} service${maintenance.services_overdue === 1 ? '' : 's'} overdue`,
      detail: 'Past the hour meter at which they were due',
      to: '/maintenance',
    });
  }

  if (today.achievement_pct != null && today.achievement_pct < 80) {
    out.push({
      key: 'behind',
      tone: today.achievement_pct < 60 ? 'red' : 'amber',
      what: `Production at ${num(today.achievement_pct, 1)} % of target`,
      detail: `${num(today.tonnes_production)} t against ${num(today.target_tonnes)} t`,
      to: '/production',
    });
  }

  // The month, against the share of its target due by today. Only on the
  // pro-rata basis: against the whole month every day but the last is behind.
  const prod = d.production;
  if (prod.target_basis === 'pro_rata' && prod.achievement_mtd_pct != null
      && prod.target_mtd && prod.achievement_mtd_pct < 80) {
    out.push({
      key: 'behind-mtd',
      tone: prod.achievement_mtd_pct < 60 ? 'red' : 'amber',
      what: `Month at ${num(prod.achievement_mtd_pct, 1)} % of target to date`,
      detail: `${num(prod.tonnes_mtd)} t against ${num(prod.target_mtd)} t due by today · `
        + `${num(Math.max(prod.target_mtd - prod.tonnes_mtd, 0))} t behind`,
      to: '/production',
    });
  }

  if (parts.critical_parts_short > 0) {
    out.push({
      key: 'crit-parts',
      tone: 'amber',
      what: `${parts.critical_parts_short} critical part${parts.critical_parts_short === 1 ? '' : 's'} out of stock`,
      detail: 'A shortage here stops a machine when it fails',
      to: '/parts',
    });
  }

  if (maintenance.services_due > 0) {
    out.push({
      key: 'svc-due',
      tone: 'amber',
      what: `${maintenance.services_due} service${maintenance.services_due === 1 ? '' : 's'} due soon`,
      detail: 'Schedule them before they fall overdue',
      to: '/maintenance',
    });
  }

  if (maintenance.liner_changes_due > 0) {
    out.push({
      key: 'liners',
      tone: 'amber',
      what: `${maintenance.liner_changes_due} liner change${maintenance.liner_changes_due === 1 ? '' : 's'} approaching`,
      detail: 'Within 100 hours of expected life',
      to: '/maintenance',
    });
  }

  // Red first, and within a tone the order they were added: machines that are
  // stopped now above things that will bite next week.
  return out.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === 'red' ? -1 : 1));
}

export function DecisionList({ data }: { data: DashboardSummary }) {
  const items = decisions(data);

  if (items.length === 0) {
    return (
      <Panel title="Needs a decision">
        <div className="issue issue--ok" style={{ alignItems: 'center' }}>
          <StatusChip tone="green">Clear</StatusChip>
          <span>
            Nothing is stopped, the blast has cover and no service is overdue.
          </span>
        </div>
      </Panel>
    );
  }

  const reds = items.filter((i) => i.tone === 'red').length;

  return (
    <Panel
      title={`Needs a decision · ${items.length}`}
      actions={reds > 0
        ? <StatusChip tone="red">{reds} stopping production</StatusChip>
        : <StatusChip tone="amber">Nothing stopped</StatusChip>}
    >
      <div className="decision-grid">
        {items.map((i) => (
          <Link key={i.key} to={i.to} className={`decision decision--${i.tone === 'red' ? 'red' : 'amber'}`}>
            <span className="decision__badge" aria-hidden>{i.tone === 'red' ? '!' : '▲'}</span>
            <span className="decision__text">
              <strong className="decision__what">{i.what}</strong>
              <span className="decision__detail">{i.detail}</span>
            </span>
            <span className="decision__go" aria-hidden>→</span>
          </Link>
        ))}
      </div>
    </Panel>
  );
}
